/**
 * WelcomePage — the "Kodeon splash" landing experience.
 *
 * Rendered inside the AppShell when the operator's `landingPage` setting is
 * `{ kind: 'kodeon-splash' }` (the default). The page itself is intentionally
 * empty — the visual is provided by `StreamParticleCanvas`, which spells the
 * word "KODEON" in particles centred in the main content area.
 *
 * Wiring:
 *   - On mount, mark particles as visible and push 'KODEON' into
 *     `useTransitionStore.particleText` so the canvas draws the word.
 *   - On unmount, clear the particle text AND mark particles as invisible
 *     so the canvas fades to fully transparent. This is what stops the
 *     dots from drifting into abstract shapes once the user navigates
 *     away — the canvas is hidden entirely while they're using the app.
 *
 * The user navigates away by picking any sidebar item — there's no in-page
 * affordance to interact with.
 */

import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import { useTransitionStore } from '@/shared/stores'

const WELCOME_TEXT = 'KODEON'

export function WelcomePage() {
  useEffect(() => {
    const store = useTransitionStore.getState()
    store.setParticlesVisible(true)
    store.setParticleText(WELCOME_TEXT)
    return () => {
      const cleanup = useTransitionStore.getState()
      // Clear the text when navigating away so the next page doesn't see
      // particles spelling 'KODEON' overlaid on its own UI. Only clear if
      // the text is still ours — another component may have set its own
      // text in the meantime (unlikely, but safe).
      if (cleanup.particleText === WELCOME_TEXT) {
        cleanup.setParticleText(null)
      }
      // Hide the canvas entirely. Without this the particles would drift
      // into abstract idle shapes and remain visible behind the user's
      // chosen page. This is the explicit "particles disappear once the
      // user is using the app" behaviour.
      cleanup.setParticlesVisible(false)
    }
  }, [])

  // The actual visual is the particle canvas. We render an empty <main>-style
  // wrapper that takes up the content area so the rest of the AppShell layout
  // (sidebar, header, session panel) sits at the right size.
  //
  // The `data-particle-text-region` attribute tells `genTextPoints` (in
  // StreamParticleCanvas) to render the KODEON text large and centred
  // within this element's bounding rect, instead of using the small
  // top-left positioning intended for operator initials on the OperatorSelector.
  return (
    <div
      style={styles.container}
      data-testid="welcome-page"
      data-particle-text-region="welcome"
      aria-label="Kodeon Stream"
    >
      {/* Intentionally empty — particles are rendered by StreamParticleCanvas. */}
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
  },
}
